import { clx, LabelRegular } from "@hm/fundamental"
import { forwardRef, useState } from "react"

export interface BaseControlledTextareaProps
	extends React.AllHTMLAttributes<HTMLTextAreaElement> {
	name: string
	label: string
	state?: "default" | "success" | "warning" | "error"
	hint?: string
	resizeable?: boolean
	showValidationErrorOnTouched?: boolean
	value?: string
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
	isTouched?: boolean
	isDisabled?: boolean
	isReadOnly?: boolean
	error?: string
}

interface StyleProps {
	bg: string
	text: string
	border: string
	hint: string
}

type InputStates = "default" | "success" | "warning" | "error"
type InputStyleStates = InputStates | "disabled" | "readOnly"

const inputStateToStyleMap: Record<InputStyleStates, StyleProps> = {
	default: {
		bg: "bg-container",
		border: "border-border_highlight_10 focus:border-primary",
		text: "text-foreground",
		hint: "text-foreground_faded",
	},
	success: {
		bg: "bg-container",
		border: "border-border_highlight_10  hover:border-success",
		text: "text-positive",
		hint: "text-positive",
	},
	warning: {
		bg: "bg-container hover:bg-container_background_hover",
		border: "border-warning",
		text: "text-warning",
		hint: "text-warning",
	},
	error: {
		bg: "bg-container hover:bg-container_background_hover",
		border: "border-negative",
		text: "text-negative",
		hint: "text-negative",
	},
	disabled: {
		bg: "bg-container_background",
		border: "border-border_highlight_10",
		text: "text-foreground_faded",
		hint: "text-foreground_faded",
	},
	readOnly: {
		bg: "bg-transparent",
		border: "border-transparent",
		text: "text-foreground",
		hint: "text-foreground_faded",
	},
}

const inputFocusBorder = "border-primary"

const getInputStyle = (
	meta: { touched?: boolean; error?: string },
	isActive: boolean,
	readOnly: boolean,
	disabled: boolean,
	state: InputStyleStates,
	showValidationErrorOnTouched: boolean,
) => {
	let key: InputStyleStates = state

	if (readOnly) {
		key = "readOnly"
	} else if (disabled) {
		key = "disabled"
	} else if ((!showValidationErrorOnTouched || meta.touched) && meta.error) {
		key = "error"
	} else if (meta.touched && !meta.error) {
		key = "success"
	}

	return {
		bg: inputStateToStyleMap[key].bg,
		border:
			isActive && !readOnly && !disabled
				? inputFocusBorder
				: inputStateToStyleMap[key].border,
		text: inputStateToStyleMap[key].text,
		hint:
			// Hint should change its color only if has error or user defines input state.
			meta.error || state !== "default"
				? inputStateToStyleMap[key].hint
				: inputStateToStyleMap.default.hint,
	}
}

const labelRegularStyle = "font-Inter font-normal text-12px leading-16px"
const textMediumStyle = "font-Inter font-medium text-16px leading-24px"

export const BaseControlledTextArea = forwardRef<
	HTMLTextAreaElement,
	BaseControlledTextareaProps
>(
	(
		{
			label,
			state = "default",
			showValidationErrorOnTouched = true,
			hint,
			isDisabled = false,
			isReadOnly = false,
			isTouched = false,
			onFocus,
			onBlur,
			value,
			resizeable = true,
			error,
			onChange,
			name,
			...props
		},
		ref,
	) => {
		const [isActive, setIsActive] = useState(!!(value && value !== ""))

		const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
			setIsActive(true)
			onFocus?.(event)
		}

		const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
			setIsActive(!!(value && value !== ""))
			onBlur?.(e)
		}

		const styling = getInputStyle(
			{ error, touched: isTouched },
			isActive,
			isReadOnly,
			isDisabled,
			state,
			showValidationErrorOnTouched,
		)

		const hasError = (!showValidationErrorOnTouched || isTouched) && !!error
		const displayHint = !hasError && hint

		return (
			<label
				className={clx(
					"block w-full",
					!isDisabled && !isReadOnly
						? "cursor-text"
						: "cursor-default",
				)}
			>
				<div
					className={clx(
						"w-full rounded-md border-1px p-2px transition-colors",
						styling.bg,
						styling.border,
						styling.text,
					)}
				>
					<div
						className={clx(
							isActive ? labelRegularStyle : textMediumStyle,
							isActive
								? "top-0 mt-4px"
								: "top-[11px] mt-1px -mb-3px",
							"relative px-12px text-foreground_faded transition-all",
						)}
					>
						{label}
					</div>

					<textarea
						ref={ref}
						name={name}
						value={value}
						onChange={onChange}
						className={clx(
							"relative block w-full min-h-[110px] px-12px pt-12px pb-4px bg-transparent text-foreground outline-none rounded-md",
							!resizeable || isDisabled || isReadOnly
								? "resize-none"
								: "",
							!isDisabled && !isReadOnly
								? "cursor-text"
								: "cursor-default",
							textMediumStyle,
							props.className,
						)}
						disabled={isDisabled}
						readOnly={isReadOnly}
						onFocus={handleFocus}
						onBlur={handleBlur}
						{...props}
					/>
				</div>

				{(hasError || displayHint) && (
					<LabelRegular
						className={clx("block mt-3px px-12px", styling.hint)}
					>
						{hasError ? error : hint}
					</LabelRegular>
				)}
			</label>
		)
	},
)
